// @ts-nocheck
import create from 'zustand';
import { immer } from 'zustand/middleware/immer';
import _ from 'lodash';
import { toast } from "sonner";
import { axios } from "./axios";

const baseUrl = `${process.env.API_URL}/api/v1`;

const createAuthHeader = (token) => {
  return {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  };
};

const useAdminStore = create(immer((set) => ({
  admin: {},
  admins: [],
  adminsByClerkId: {} as Record<string, any>,
  // Methods to manipulate the state
  setAdmin: (admin) => {
    set((state) => {
      state.admin = admin;
    });
  },
  postLeagueBanner: async (image, leagueName, admin_id, token, cb) => {
    try {
      const data = new FormData();
      data.append("upload", image);

      // Let axios auto-set `Content-Type: multipart/form-data; boundary=…`
      // off the FormData payload. Setting it manually here would inject
      // `boundary=${data._boundary}` — and `_boundary` is a Node-only field on
      // the `form-data` package; in a browser it's `undefined`, which the
      // server's multer parser can't decode.
      const config = {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      };
      // Route lives on the organization router (apps/api/api/api.ts:12), not the
      // league router. The previous `/leagues/...` path 404'd silently.
      const response = await axios.post(
        `${baseUrl}/organizations/d/${leagueName}/bannerupload2/${admin_id}`,
        data,
        config,
      );
      // Unwrap our standard { error, result, status } envelope from response.data
      // (the prior destructure read these off the axios response itself, so
      // `result` was always undefined — that's why the callback never received
      // the updated org doc).
      const { error, result } = response.data || {};

      if (error) toast.error(error);
      else toast.success("Successfully Added!");

      // Don't write to state.admin here — `result` is the updated Organization
      // doc, not an Admin. Assigning it here corrupts the admin slice for every
      // other consumer of useAdminStore().admin.
      cb && cb(result);
    } catch (error) {
      console.error("Error in postLeagueBanner:", error);
      toast.error("Failed to upload banner");
      cb && cb(null);
    }
  },
  generateBanner: async (orgId, input, token) => {
    const { data } = await axios.post(
      `${baseUrl}/organizations/${orgId}/banners`,
      input,
      createAuthHeader(token)
    );
    return data;
  },
  getBannerJobStatus: async (jobId, token) => {
    const { data } = await axios.get(
      `${baseUrl}/jobs/${jobId}?queue=banners`,
      createAuthHeader(token)
    );
    return data?.result || data;
  },
  selectBannerVariant: async (orgId, variantUrl, token) => {
    const { data } = await axios.post(
      `${baseUrl}/organizations/${orgId}/banners/select`,
      { variantUrl },
      createAuthHeader(token)
    );
    return data?.result || data;
  },
  // Logo asset actions (2026-09-09) — mirror the banner four exactly; the
  // FormData/boundary and envelope-unwrap caveats above apply here too.
  postLeagueLogo: async (image, leagueName, admin_id, token, cb) => {
    try {
      const data = new FormData();
      data.append("upload", image);
      const config = {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      };
      const response = await axios.post(
        `${baseUrl}/organizations/d/${leagueName}/logoupload2/${admin_id}`,
        data,
        config,
      );
      const { error, result } = response.data || {};
      if (error) toast.error(error);
      else toast.success("Logo uploaded");
      cb && cb(result);
    } catch (error) {
      console.error("Error in postLeagueLogo:", error);
      toast.error("Failed to upload logo");
      cb && cb(null);
    }
  },
  // Custom premier hero photo upload (INT-0013) — same shape as
  // postLeagueLogo (FormData boundary + envelope caveats apply).
  postLeagueHero: async (image, leagueName, admin_id, token, cb) => {
    try {
      const data = new FormData();
      data.append("upload", image);
      const config = {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      };
      const response = await axios.post(
        `${baseUrl}/organizations/d/${leagueName}/heroupload2/${admin_id}`,
        data,
        config,
      );
      const { error, result } = response.data || {};
      if (error) toast.error(error);
      else toast.success("Hero photo uploaded");
      cb && cb(result);
    } catch (error) {
      console.error("Error in postLeagueHero:", error);
      toast.error("Failed to upload hero photo");
      cb && cb(null);
    }
  },
  generateLogo: async (orgId, input, token) => {
    const { data } = await axios.post(
      `${baseUrl}/organizations/${orgId}/logos`,
      input,
      createAuthHeader(token)
    );
    return data;
  },
  getLogoJobStatus: async (jobId, token) => {
    const { data } = await axios.get(
      `${baseUrl}/jobs/${jobId}?queue=logos`,
      createAuthHeader(token)
    );
    return data?.result || data;
  },
  selectLogoVariant: async (orgId, variantUrl, token) => {
    const { data } = await axios.post(
      `${baseUrl}/organizations/${orgId}/logos/select`,
      { variantUrl },
      createAuthHeader(token)
    );
    return data?.result || data;
  },
  // Phone hand-off (INT-0038): email the signed-in admin a /setup link.
  // Rethrows so the caller can map a 429 (throttled) to its own toast.
  sendHandoffEmail: (getToken) => async () => {
    const token = await getToken();
    const { data } = await axios.post(`${baseUrl}/admins/auth/handoff-email`, {}, createAuthHeader(token));
    return data?.result || null;
  },
  getMemberships: (getToken) => {
    return async (orgId, cb) => {
      try {
        const token = await getToken();
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        const {error, result, status } = (await axios.get(`${baseUrl}/admins/memberships/${orgId}`, config)).data;
        console.log('getMemberships::result: ', result);

        cb && cb(result);
      } catch (error) {
        console.error("Error in getMemberships:", error);
      }
    }
  },
  removeMembership: (getToken) => {
    return async (membership, cb) => {
      try {
        const token = await getToken();
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        const { error, result, status } = (await axios.post(`${baseUrl}/admins/removeMembership/${membership.userId}`, membership, config)).data;
        error ? toast.error(error) : toast.success(`Successfully Removed`);
        cb && cb(result);
      } catch (error) {
        console.error("Error in removeMembership:", error);
      }
    }
  },
  revokeInvitation: (getToken) => {
    return async (invitation, cb) => {
      try {
        const token = await getToken();
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        const { error, result } = (await axios.post(`${baseUrl}/admins/revokeInvitation`, invitation, config)).data;
        if (error) toast.error(error);
        else toast.success('Invitation revoked');
        cb && cb(result);
      } catch (error: any) {
        console.error('Error in revokeInvitation:', error);
        toast.error(error?.response?.data?.error || error?.message || 'Failed to revoke invitation');
      }
    }
  },
  promoteMembership: (getToken) => {
    return async (membershipData, cb) => {
      try {
        const token = await getToken();
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        const { error, result, status } = (await axios.post(`${baseUrl}/admins/promoteMembership`, membershipData, config)).data;
        error ? toast.error(error) : toast.success(`Successfully Promoted!`);
        cb && cb(result);
      } catch (error) {
        console.error("Error in promoteMembership:", error);
      }
    }
  },
  getAdminsByLeagueName: (getToken) => {
    return async (leagueName, cb) => {
      try {
        const token = await getToken();
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        const {error, result, status } = (await axios.get(`${baseUrl}/admins/organization/${leagueName}`, config)).data;
        if (result) {
          set((state) => {
            state.admins = result;
          });
        }
        console.log('getAdminsByLeagueName::result: ', result);
        cb && cb(result);
      } catch (error) {
        console.error("Error in getAdminsByLeagueName:", error);
      }
    }
  },


  getInvitations: (getToken) => {
    return async (orgId, cb) => {
      try {
        const token = await getToken();
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        const {error, result, status } = (await axios.get(`${baseUrl}/admins/invitations/${orgId}`, config)).data;
        console.log('getInvitations::result: ', result);

        cb && cb(result);
      } catch (error) {
        console.error("Error in getInvitations:", error);
      }
    }
  },
  updateAdmin: (getToken) => {
    return async (admin, cb) => {
      try {
        const token = await getToken();
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        const { error, result, status } = (await axios.put(`${baseUrl}/admins/${admin.clerkId}`, admin, config)).data;
        error ? toast.error(error) : toast.success(`Successfully Updated!`);
        if (result) {
          set((state) => {
            state.admin = result;
          });
        }
        debugger;
        cb && cb(result); 
      } catch (error) {
        console.error("Error in updateAdmin:", error);
      }
    }
  },
    // Asynchronous action
    getAdminByClerkId:  (getToken) => {
    return async (id, cb) => {
      try {
        const token = await getToken();
        console.log('getAdminByClerkId::token: ', token);
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        const { error, result, status } = (await axios.get(`${baseUrl}/admins/clerk/${id}?include=organizations`, createAuthHeader(token))).data;
        console.log('getAdminByClerkId::data: ', result);
        if (result) {
          set((state) => {
            state.admin = result;
          });
        }
        cb && cb(result);
      } catch (error) {
        console.error("Error in getAdminByClerkId:", error);
        // Callers chain org-resolution fallbacks off this callback — swallowing
        // the error here used to strand them with no org at all.
        cb && cb(null);
      }
    }
  },
  postInvitation: (getToken) => {
    return async (email, organization, userId, firstName, lastName, cb, initialScopes?, role?) => {
      try {
        const token = await getToken();
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        const { error, result, status } = (await axios.post(`${baseUrl}/admins/invitation`,
          { email, organization, userId, firstName, lastName, initialScopes, role },
          config
          )).data;
        error ? toast.error(error) : toast.success(`Successfully Sent!`);

        cb && cb(result);
      } catch (error) {
        console.error("Error in postInvitation:", error);
      }
    }
  },
  updateAdminScopes: (getToken) => {
    return async (clerkId, scopes, cb?) => {
      try {
        const token = await getToken();
        const config = { headers: { 'Authorization': `Bearer ${token}` } };
        const { error, result } = (await axios.patch(
          `${baseUrl}/admins/clerk/${clerkId}/scopes`,
          { accessScopes: scopes },
          config
        )).data;
        error ? toast.error(error) : toast.success('Permissions updated!');
        cb && cb(result);
      } catch (error) {
        console.error('Error in updateAdminScopes:', error);
      }
    };
  },
  getAdminsByClerkIds: (getToken) => {
    return async (clerkIds, cb?) => {
      try {
        const token = await getToken();
        const config = { headers: { 'Authorization': `Bearer ${token}` } };
        const { error, result } = (await axios.post(
          `${baseUrl}/admins/by-clerk-ids`,
          { clerkIds },
          config
        )).data;
        if (result) {
          // Key primarily by Mongo _id — MemberAdmin looks up adminDoc by
          // membership.publicUserData.userId, which is now the admin _id. Also
          // mirror provider.id (legacy Clerk userId) so either key resolves.
          const byClerkId = {};
          result.forEach((admin) => {
            if (admin?._id) byClerkId[String(admin._id)] = admin;
            if (admin?.provider?.id) byClerkId[admin.provider.id] = admin;
          });
          set((state) => {
            state.adminsByClerkId = byClerkId;
          });
        }
        cb && cb(result);
      } catch (error) {
        console.error('Error in getAdminsByClerkIds:', error);
      }
    };
  },


})));

export default useAdminStore;
